import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';

import {
  UserRole,
  UserState,
  CreateUserPayload,
  UpdateUserPayload,
  CreateRolePayload,
  UpdateRolePayload
} from './types';
import { httpClient } from '@lib/httpClient';
import { PHOTOS_DOMAIN } from 'src/config';

const normalizeImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;

  const cleaned = url.startsWith('/') ? url.substring(1) : url;
  return `${PHOTOS_DOMAIN}${cleaned}`;
};

const authHeader = (token: string | null) =>
  token ? { Authorization: `Bearer ${token}` } : {};

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: null,
        role: null,
        remember: false,
        adminUsers: [],
        corretores: [],
        roles: [],

        // ─── Auth ──────────────────────────────────────────────────────────
        logIn: async (email: string, password: string, remember: boolean) => {
          try {
            const { data } = await httpClient.post('/api/login', {
              email,
              password
            });
            data.user.avatar = normalizeImageUrl(data.user.avatar) || null;
            set({
              user: data.user,
              token: data.token,
              role: data.user.role_id as UserRole,
              remember
            });
            return true;
          } catch (error) {
            console.error('Login failed:', error);
            return false;
          }
        },
        logOut: async () => {
          try {
            const token = get().token;
            if (token) {
              await httpClient.post(
                '/api/logout',
                {},
                { headers: authHeader(token) }
              );
            }
          } catch (error) {
            console.error('Logout request failed:', error);
          }
          set({ user: null, token: null, remember: false, role: null });
        },
        updateUserAvatar: async (file: File) => {
          try {
            if (!get().user) return false;
            const avatarUrl = await get().uploadUserAvatar(file);
            if (!avatarUrl) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${get().token}`;
            const { data } = await httpClient.patch('/api/usuarios/avatar', {
              avatar: avatarUrl
            });
            console.log('Avatar updated successfully:', data);
            set({
              user: {
                ...get().user!,
                avatar: normalizeImageUrl(avatarUrl)
              }
            });
            return true;
          } catch (error) {
            console.error('Failed to update user avatar:', error);
            return false;
          }
        },
        uploadUserAvatar: async (file: File): Promise<string | null> => {
          const formData = new FormData();
          formData.append('image', file);

          try {
            const response = await httpClient.post(
              '/api/usuarios/upload/imagem',
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  Authorization: `Bearer ${get().token}`
                },
                timeout: 60000
              }
            );

            return response.data.url || null;
          } catch (erro) {
            console.error('Avatar upload failed:', {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type
            });
            return null;
          }
        },

        // ─── Auth – password recovery ──────────────────────────────────────
        forgotPassword: async (email: string) => {
          try {
            await httpClient.post('/api/forgot-password', { email });
            return true;
          } catch (error) {
            console.error('Forgot password failed:', error);
            return false;
          }
        },
        changePassword: async (
          currentPassword: string,
          newPassword: string
        ) => {
          try {
            const { data } = await httpClient.post(
              '/api/mudar-senha',
              {
                current_password: currentPassword,
                new_password: newPassword,
                new_password_confirmation: newPassword
              },
              { headers: authHeader(get().token) }
            );
            // Keep the session alive with the newly issued token
            if (data.token) {
              set({ token: data.token });
            }
            return { ok: true };
          } catch (error) {
            const err = error as {
              response?: {
                data?: {
                  errors?: { current_password?: string[] };
                  message?: string;
                };
              };
            };
            const msg =
              err?.response?.data?.errors?.current_password?.[0] ||
              err?.response?.data?.message ||
              'Erro ao alterar senha.';
            return { ok: false, message: msg };
          }
        },
        fetchMyProfile: async () => {
          try {
            const { data } = await httpClient.get('/api/user', {
              headers: authHeader(get().token)
            });
            set({ user: data });
          } catch (error) {
            console.error('Failed to fetch user profile:', error);
          }
        },
        checkShouldLogoutOnBoot: async () => {
          try {
            const token = get().token;
            if (!token) return false;

            const { data } = await httpClient.post(
              '/api/usuarios/verificar-should-logout',
              {},
              { headers: authHeader(token) }
            );

            if (data?.should_logout === true) {
              set({ user: null, token: null, remember: false, role: null });
              return true;
            }

            return false;
          } catch (error) {
            console.error('Failed to check forced logout:', error);
            return false;
          }
        },
        updateOwnProfile: async (data: { cellphone?: string }) => {
          try {
            const { data: updated } = await httpClient.patch(
              '/api/minha-conta',
              data,
              { headers: authHeader(get().token) }
            );
            set({ user: updated.usuario });
            return true;
          } catch (error) {
            console.error('Failed to update own profile:', error);
            return false;
          }
        },
        saveEditorPreferences: async (data: {
          pref_descricao_height?: string;
          pref_info_adicional_height?: string;
        }) => {
          try {
            const { data: updated } = await httpClient.patch(
              '/api/minha-conta',
              data,
              { headers: authHeader(get().token) }
            );
            set({ user: updated.usuario });
            return true;
          } catch (error) {
            console.error('Failed to save editor preferences:', error);
            return false;
          }
        },

        // ─── Admin – user management ───────────────────────────────────────
        fetchUsers: async () => {
          try {
            const { data } = await httpClient.get('/api/usuarios', {
              headers: authHeader(get().token)
            });
            set({ adminUsers: data });
          } catch (error) {
            console.error('Failed to fetch users:', error);
          }
        },
        fetchCorretores: async () => {
          try {
            const { data } = await httpClient.get('/api/corretores', {
              headers: authHeader(get().token)
            });
            set({ corretores: data });
          } catch (error) {
            console.error('Failed to fetch corretores:', error);
          }
        },
        createUser: async (payload: CreateUserPayload) => {
          try {
            await httpClient.post('/api/usuarios', payload, {
              headers: authHeader(get().token)
            });
            await get().fetchUsers();
            return true;
          } catch (error) {
            console.error('Failed to create user:', error);
            return false;
          }
        },
        updateUser: async (id: number, payload: UpdateUserPayload) => {
          try {
            await httpClient.put(`/api/usuarios/${id}`, payload, {
              headers: authHeader(get().token)
            });
            await get().fetchUsers();
            return true;
          } catch (error) {
            console.error('Failed to update user:', error);
            return false;
          }
        },
        toggleUser: async (id: number) => {
          try {
            await httpClient.patch(
              `/api/usuarios/${id}/toggle`,
              {},
              { headers: authHeader(get().token) }
            );
            await get().fetchUsers();
            return true;
          } catch (error) {
            console.error('Failed to toggle user:', error);
            return false;
          }
        },
        deleteUser: async (id: number) => {
          try {
            await httpClient.delete(`/api/usuarios/${id}`, {
              headers: authHeader(get().token)
            });
            await get().fetchUsers();
            return true;
          } catch (error) {
            console.error('Failed to delete user:', error);
            return false;
          }
        },

        // ─── Admin – role management ───────────────────────────────────────
        fetchRoles: async () => {
          try {
            const { data } = await httpClient.get('/api/roles', {
              headers: authHeader(get().token)
            });
            set({ roles: data });
          } catch (error) {
            console.error('Failed to fetch roles:', error);
          }
        },
        createRole: async (payload: CreateRolePayload) => {
          try {
            await httpClient.post('/api/roles', payload, {
              headers: authHeader(get().token)
            });
            await get().fetchRoles();
            return true;
          } catch (error) {
            console.error('Failed to create role:', error);
            return false;
          }
        },
        updateRole: async (id: number, payload: UpdateRolePayload) => {
          try {
            await httpClient.put(`/api/roles/${id}`, payload, {
              headers: authHeader(get().token)
            });
            await get().fetchRoles();
            return true;
          } catch (error) {
            console.error('Failed to update role:', error);
            return false;
          }
        },
        toggleRole: async (id: number) => {
          try {
            await httpClient.patch(
              `/api/roles/${id}/toggle`,
              {},
              { headers: authHeader(get().token) }
            );
            await get().fetchRoles();
            return true;
          } catch (error) {
            console.error('Failed to toggle role:', error);
            return false;
          }
        }
      }),
      {
        name: 'user-session',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
          role: state.role,
          token: state.token,
          remember: state.remember
        })
      }
    )
  )
);
